import { isObject } from 'lodash'
import { usePathname } from 'next/navigation'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useLocalStorage, useUpdateEffect } from 'react-use'

import { useCurrentUser } from '~/data/api/hooks/users'
import useLogger from '~/debug/logger'

type StoredValue<T, C> = { context: C; value: T }
type EvictionFn = (...reasons: string[]) => void
type UseLocalStorageStateReturn<T, C> = [T, UpdateCallback<T, C>, EvictionFn]

// The context argument must be omitted if the stored value isn't tracking one
type UpdateCallback<T, C> = C extends undefined ? (value: T) => void : (value: T, context: C) => void

type InvariantFn<T, C> = (value: StoredValue<T, C>) => string | null
type Options<T, C> = OptionsWithDefault<T> | OptionsWithoutDefault<T, C>
type CommonOptions = {
  /**
   * If true, the key will not be stored in local storage. This can be useful in shared components
   * when one consuming component needs to use local storage but another doesn't.
   */
  skip?: boolean

  /**
   * By default, this hook isolates local storage items by page. If `ignorePath` is true, the
   * current pathname will not be included in the local storage key. Useful for storing items that
   * are not page-specific.
   */
  ignorePath?: boolean

  /**
   * Optional callback that can be used to customize the path before it is included in the LocalStorage
   * key name. This can be useful for tables that are rendered on pages with distinct paths but which
   * should share options.
   */
  pathTransform?: (path: string) => string

  /**
   * Causes the current user's ID to be included in the local storage key. This allows items to not
   * collide and remain separate when switching between accounts
   */
  userSpecific?: boolean
}

type OptionsWithDefault<T> = CommonOptions & {
  /** The default value for the state if no value is found in local storage. */
  defaultValue: T
}

type OptionsWithoutDefault<T, C> = CommonOptions & {
  /**
   * An invariant function that determines if the value in local storage is valid. The function will
   * be called with the stored value and context. If the value is valid, the function should return
   * null. If the value is invalid, the function should return a string describing why the value is
   * invalid; in this case, the value will be evicted from local storage.
   */
  invariantFn?: InvariantFn<T, C>
}

// If no initial value is provided, the return type is `T | null`
export function useLocalStorageState<T, C = undefined>(
  key: string,
  options?: OptionsWithoutDefault<T, C>
): UseLocalStorageStateReturn<T | null, C>

// If an initial value is provided, the return type is `T`
export function useLocalStorageState<T, C = undefined>(
  key: string,
  options: OptionsWithDefault<T>
): UseLocalStorageStateReturn<T, C>

export function useLocalStorageState<T, C>(key: string, options: Options<T, C> = {}) {
  const logger = useLogger('useLocalStorageState')
  const pathname = usePathname()
  const transformedPath = options.pathTransform ? options.pathTransform(pathname) : pathname

  // Parse options
  const [defaultValue, invariantFn, skip, ignorePath, userSpecific] = useMemo(() => {
    const { skip = false, ignorePath = false, userSpecific = false } = options
    const commonOptions = [skip, ignorePath, userSpecific]
    if ('defaultValue' in options) {
      return [options.defaultValue, null, ...commonOptions]
    } else {
      return [null, options.invariantFn, ...commonOptions]
    }
  }, [options])

  const [value, setValue] = useState(defaultValue)
  const { synopUser } = useCurrentUser()
  const fullKey = [userSpecific && synopUser ? synopUser.id + '/' : '', ignorePath ? '' : transformedPath, key].join('')

  // A `defaultValue` may not be provided alongside an `invariantFn`, so
  // if a default value is provided we know the context is `undefined`. Thus
  // this is a safe cast. Null-checked rather than truthy-checked so falsy
  // defaults (`false`, `0`, `''`) still seed storage.
  const initialValue = defaultValue !== null ? ({ value: defaultValue } as StoredValue<T, C>) : null

  // If `skip` is true, `storedValue` will always be null. This ensures that the
  // key remains outside of LocalStorage.
  const [storedValue, setStoredValue, removeValue] = useLocalStorage<StoredValue<T, C> | null>(
    fullKey,
    skip ? null : initialValue
  )

  const evictKey = useCallback<EvictionFn>(
    (...reasons) => {
      const msg = `evicting key "${fullKey}" from LocalStorage`
      logger.info([msg, ...reasons].join(': '))
      removeValue()
    },
    [fullKey, logger, removeValue]
  )

  const evictIfViolatesInvariant = useCallback(
    (value?: StoredValue<T, C> | null) => {
      const evictionReason = value && invariantFn?.(value)
      if (evictionReason) {
        evictKey('invariant violated', evictionReason)
      }
    },
    [evictKey, invariantFn]
  )

  const update = useCallback(
    (newValue: T, context: C) => {
      setValue(newValue)
      if (!skip) setStoredValue({ value: newValue, context })
    },
    [setStoredValue, skip]
  )

  // On the first render, check if the stored value is valid and evict it if not. On top
  // of that, check if it has the expected format (i.e. a `value` key). If not evict.
  const checkedValidity = useRef(false)
  if (!checkedValidity.current && storedValue) {
    checkedValidity.current = true

    const unvalidated = storedValue as unknown
    if (isObject(unvalidated) && 'value' in unvalidated) {
      evictIfViolatesInvariant(storedValue)
    } else {
      evictKey('invalid format')

      // If there's a default value, set it
      if (defaultValue !== null) {
        update(defaultValue, undefined as unknown as C)
      }
    }
  }

  // Whenever the eviction function changes (i.e. the invariant function changes)
  // call it to see if the value in LocalStorage is still valid
  useUpdateEffect(() => {
    evictIfViolatesInvariant(storedValue)
  }, [evictIfViolatesInvariant])

  const returnValue = useMemo(() => {
    // Not using local storage. Return the value from React state
    if (skip) return value
    // A (validated) value is in local storage. The record's presence, not the
    // value's truthiness, decides — a stored `false`/`0`/`''` is a real value.
    if (storedValue) return storedValue.value
    // A default value was provided. This can happen during the first render when a key
    // had to be evicted. The `update` function was called with the default value, but
    // the `storedValue` variable still has the initial value pulled from local storage.
    if (defaultValue !== null) return defaultValue
    // No value in local storage and no default value. Return null
    return null
  }, [skip, defaultValue, storedValue, value])

  return [returnValue, update, evictKey]
}
